/* eslint-disable no-use-before-define */
import React, { useState, useEffect } from 'react';
import _ from 'underscore';
import { titleize } from 'underscore.string';
import PropTypes from 'prop-types';
import InvoiceDetails from 'components/modules/accounting/invoices/invoice-details';
import CargomaticModal from 'components/v2/modal';
import { getInvoicesForShipment } from 'utils/AccountingWebAPIUtils';
import CircleCheckIcon from 'components/v2/icons/CircleCheckIcon';
import Tooltip from 'components/v2/Tooltip';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import SessionStore from 'stores/SessionStore';

import formatMoney from 'utils/formatters/Money';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import AddBaseCostForm from './AddBaseCostForm';
import AdjustmentTitle from './AdjustmentTitle';
import AdjustmentAction from './AdjustmentAction';
import DateField from './DateField';
import { sluggify } from '../../../../utils/localization/utils';
import ExternalLink from '../../../v2/ExternalLink';

const shouldRenderCreateCreditMemoButton = (invoice, render = false) => {
  if (!invoice || !render) return false;
  const negativeItems = _.flatten(
    invoice?.lineItemGroups.map((lineItemGroup) =>
      lineItemGroup?.lineItems.filter((lineItem) => lineItem?.value < 0),
    ),
  );
  if (negativeItems.length) return false;
  return true;
};

export default function ShipperCharges({
  shipmentId,
  shipmentIsCurrent,
  isIntermodalShipmentType,
  charges,
  timezone,
  total,
  currency,
  onAddCustomAdjustment,
  onAddShipperCostAdjustment,
  onAddInternalAdjustment,
  onAddCustomerAdjustment,
  onAddBaseCost,
  onRemoveLineItem,
}) {
  const { t } = useTranslation();
  const [lineItems, setLineItems] = useState([]);
  const [selectedLineItem, setSelectedLineItem] = useState();
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showConfirmRemovalModal, setShowConfirmRemovalModal] = useState(false);
  function handleHideDetailsModal() {
    setShowDetailsModal(false);
  }

  async function getInvoices() {
    try {
      const {
        entity: { invoices },
      } = await getInvoicesForShipment(shipmentId);
      const creditMemos = invoices.filter((i) => i.original_invoice);
      const mappedLineItems = charges.map((lineItem, i) => {
        const invoice = invoices.find((p) => p._id === lineItem.invoice_id);
        const hasCreditMemo = creditMemos.find(
          (p) => p.original_invoice === lineItem.invoice_id,
        );
        const isFirstFuelSurchargeItem = charges.find(c => c.charge_code === 'FSC')?._id === lineItem._id
        let isCreditMemo = false;
        if (
          invoice &&
          invoice.invoice_type &&
          invoice.invoice_type.toLowerCase() === 'credit'
        ) {
          isCreditMemo = true;
        }
        if (!invoice) {
          return {
            ...lineItem,
            hasInvoice: false,
            hasCreditMemo: false,
            isCreditMemo,
            invoice: null,
            renderCreditMemoActionButton: false,
            isFirstFuelSurchargeItem
          };
        }

        const {
          _id,
          number,
          timestamp,
          status,
          email_sent,
          edi_sent,
          created_by,
          original_invoice,
        } = invoice;
        return {
          ...lineItem,
          hasInvoice: true,
          hasCreditMemo: !!hasCreditMemo,
          isCreditMemo,
          invoice: {
            _id,
            number,
            timestamp,
            status,
            email_sent,
            edi_sent,
            created_by,
            original_invoice,
          },
          renderCreditMemoActionButton: shouldRenderCreateCreditMemoButton(
            invoice,
            !isCreditMemo && !hasCreditMemo,
          ),
          isFirstFuelSurchargeItem
        };
      });

      setLineItems(mappedLineItems);
    } catch (e) {
      console.log('Error getting invoices', e);
    }
  }

  function handleRemove(lineItem) {
    return function () {
      setShowConfirmRemovalModal(true);
      setSelectedLineItem(lineItem);
    };
  }

  function handleShowDetails(_id) {
    return () => {
      setSelectedLineItem(_id);
      setShowDetailsModal(true);
    };
  }

  async function handleConfirmLineItemRemoval() {
    await onRemoveLineItem(selectedLineItem, 'receivables');
    handleHideConfirmRemovalModal();
  }

  function handleHideConfirmRemovalModal() {
    setShowConfirmRemovalModal(false);
    setSelectedLineItem(null);
  }

  function showRemoveButton({ processed, accessorial_request_id }) {
    return (
      (SessionStore.isFinance() || SessionStore.isFinanceAdmin()) &&
      ((!processed && isIntermodalShipmentType && !accessorial_request_id) ||
        (!isIntermodalShipmentType && !processed))
    );
  }

  function renderRemoveButton(lineItem) {
    if (showRemoveButton(lineItem)) {
      return (
        (<DeleteButton
          dataTestId="DeleteButton-aee6fb8a"
          disabled={lineItem.processed}
          onClick={handleRemove(lineItem)} />)
      );
    }
  }

  function renderInvoiceLink(lineItem) {
    if (!lineItem.invoice) {
      return null;
    }
    const {
      invoice: { _id, number },
    } = lineItem;

    return (
      <ExternalLink
        dataTestId="ExternalLink-300399a1"
        href={`/invoices/${_id}`}
        title={number}
        showIcon />
    );
  }

  // Bens recommendation for accessorials beyond the 2 week period.
  // Some might be incorrectly marked but it was a trade off decision
  function getRateSource(lineItem) {
    if (!lineItem?.rate_source) {
      return lineItem?.autorated ? t('AUTOMATED') : t('MANUAL');
    }

    return t(sluggify(lineItem.rate_source));
  }

  useEffect(() => {
    getInvoices();
  }, [charges]);

  return (
    (<div data-testid="div-b8449f7f">
      <div data-testid="div-8bd80c90" className="">
        <h3 data-testid="h3-55b1b911" className="my-0 mb-4 text-base">{t('SHIPPER_CHARGES')}</h3>
        <div data-testid="div-ac540022" className="table-wrapper overflow-x-auto">
          <table data-testid="table-5fcbbecd" className="default-table table-fixed">
            <thead data-testid="thead-686cffab">
              <tr data-testid="tr-4445a614">
                <th data-testid="th-f5f02229" className="w-48">{t('CHARGE')}</th>
                <th data-testid="th-d2210e93" className="w-20">{t('COST')}</th>
                <th data-testid="th-b772e62b" className="w-40">{t('ENTERED_DATE')}</th>
                <th data-testid="th-036952da" className="w-40">{t('RATE_SOURCE')}</th>
                <th data-testid="th-71b62c17" className="w-52">{t('INVOICE', { count: 1 })}</th>
                <th data-testid="th-6225dddc" className="w-40">{t('INVOICE_DATE')}</th>
                <th data-testid="th-5aa999cf" className="w-5">{t('EMAIL_SENT')}</th>
                <th data-testid="th-11240d0b" className="w-5">{t('EDI_SENT')}</th>
                <th data-testid="th-33b0d070" className="w-64">{titleize(t('ACTION'))}</th>
                <th data-testid="th-eefd8d8d" className="w-64">{t('REASON_CODE')}</th>
                <th data-testid="th-d5b382fa" className="w-52">{titleize(t('NOTE', { count: 3 }))}</th>
                <th data-testid="th-032e6bd5" className="w-52">{t('LAST_USER')}</th>
              </tr>
            </thead>
            <tbody data-testid="tbody-7558de9d">
              {lineItems.map((lineItem, index) => (
                <tr data-testid={`tr-ecb27b9a-${index}`} key={lineItem._id}>
                  <td data-testid={`td-3fd5ddae-${index}`} className="break-words">
                    <AdjustmentTitle
                      lineItem={lineItem}
                      unapplied={
                        isIntermodalShipmentType && !lineItem.equipment
                      }
                    />
                  </td>
                  <td data-testid={`td-ca6fdd9a-${index}`}>
                    <span
                      data-testid={`span-edaf2609-${index}`}
                      className={classnames({
                        'text-red-500': lineItem.value < 0,
                      })}>
                      {formatMoney(lineItem.value, lineItem?.currency)}
                    </span>
                  </td>
                  <td data-testid={`td-1a061458-${index}`}>
                    <DateField date={lineItem.timestamp} timezone={timezone} />
                  </td>
                  <td data-testid={`td-8fe9d102-${index}`} className="text-capitalize">{getRateSource(lineItem)}</td>
                  <td data-testid={`td-21f8b5ea-${index}`}>{renderInvoiceLink(lineItem)}</td>
                  <td data-testid={`td-8e9b2769-${index}`}>
                    <DateField
                      date={lineItem.invoice?.timestamp}
                      timezone={timezone}
                    />
                  </td>
                  <td data-testid={`td-ff087fec-${index}`}>
                    {lineItem.invoice?.email_sent && (
                      <Tooltip
                        dataTestId={`Tooltip-58641052-${index}`}
                        className="w-5 h-5"
                        body={() => (
                          <CircleCheckIcon className="text-green-cm h-5 w-5" />
                        )}>
                        <div data-testid={`div-e613b1f7-${index}`}>{t('EMAIL_SENT_WITH_EXCLAMATION')}</div>
                      </Tooltip>
                    )}
                  </td>
                  <td data-testid={`td-7c03eedf-${index}`}>
                    {lineItem.invoice?.edi_sent && (
                      <Tooltip
                        dataTestId={`Tooltip-668bdc03-${index}`}
                        className="w-5 h-5"
                        body={() => (
                          <CircleCheckIcon className="text-green-cm h-5 w-5" />
                        )}>
                        <div data-testid={`div-b371eb12-${index}`}>{t('EDI_SENT_WITH_EXCLAMATION')}</div>
                      </Tooltip>
                    )}
                  </td>
                  <td data-testid={`td-dc9eb3ea-${index}`}>
                    <div
                      data-testid={`div-c98938dc-${index}`}
                      className="flex items-center space-x-2">
                      <AdjustmentAction
                        lineItem={lineItem}
                        shipmentIsCurrent={shipmentIsCurrent}
                        isIntermodalShipmentType={isIntermodalShipmentType}
                        onAddCustomAdjustment={onAddCustomAdjustment}
                        onAddShipperCostAdjustment={(lineItem) =>
                          onAddShipperCostAdjustment(lineItem)
                        }
                        onAddCustomerAdjustment={onAddCustomerAdjustment}
                        onAddInternalAdjustment={onAddInternalAdjustment}
                      />
                      {showRemoveButton(lineItem) && (
                        <Tooltip
                          dataTestId={`Tooltip-6934443d-${index}`}
                          id="remove-line-item"
                          position="top"
                          body={() => renderRemoveButton(lineItem)}>
                          {t('REMOVE_LINE_ITEM')}
                        </Tooltip>
                      )}
                    </div>
                  </td>
                  <td data-testid={`td-1ccc26da-${index}`} className="break-all">{lineItem?.reason_code || ''}</td>
                  <td data-testid={`td-8518de51-${index}`} className="break-words">{lineItem?.notes}</td>
                  <td data-testid={`td-33e1c994-${index}`} className="break-words">{lineItem?.user}</td>
                </tr>
              ))}
              <tr data-testid="tr-e8ae2415">
                <td data-testid="td-32309f05">
                  <div data-testid="div-292f3677" className="w-48">{t('TOTAL')}</div>
                </td>
                <td data-testid="td-d7126c40" className="font-bold">
                  {formatMoney(total, lineItems[0]?.currency)}
                </td>
                <td data-testid="td-e02b9618" colSpan={12}>&nbsp;</td>
              </tr>
            </tbody>
          </table>
          <AddBaseCostForm
            ledgerType="receivables"
            ledgers={charges}
            onAddBaseCost={onAddBaseCost}
            currency={currency}
          />
        </div>
      </div>
      {showDetailsModal && (
        <CargomaticModal
          dataTestId="CargomaticModal-cfbab181"
          title=""
          show={showDetailsModal}
          footer={null}
          body={<InvoiceDetails invoiceId={selectedLineItem} />}
          onHide={handleHideDetailsModal}
          dialogClassName="modal-override"
          location="top" />
      )}
      {showConfirmRemovalModal && (
        <ConfirmModal
          dataTestId="ConfirmModal-b07649dc"
          show={showConfirmRemovalModal}
          onConfirm={handleConfirmLineItemRemoval}
          onHide={handleHideConfirmRemovalModal}
          title={t('APPROVE_ITEM_REMOVAL_TITLE')}
          message={t('APPROVE_ITEM_REMOVAL_MESSAGE')}
          affirmative={t('APPROVE')}
          hideButtonText={t('NO')} />
      )}
    </div>)
  );
}

ShipperCharges.propTypes = {
  shipmentId: PropTypes.string.isRequired,
  shipmentIsCurrent: PropTypes.bool.isRequired,
  timezone: PropTypes.string,
  isIntermodalShipmentType: PropTypes.bool,
};

ShipperCharges.defaultProps = {
  timezone: 'America/Los_Angeles',
};
